import { useScrollReveal } from '@/hooks/useScrollReveal';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Nossos Veículos', href: '#veiculos' },
  { label: 'Serviços', href: '#servicos' },
  { label: 'Sobre Nós', href: '#sobre' },
  { label: 'Depoimentos', href: '#depoimentos' },
  { label: 'Contato', href: '#contato' },
];

const services = [
  'Troca de Óleo e Filtro',
  'Sistema de Freios',
  'Mecânica Geral',
  'Suspensão',
  'Correia Dentada',
  'Fluido de Transmissão',
];

export default function Footer() {
  const { ref: contentRef, visible: contentVisible } = useScrollReveal();

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="relative border-t border-white/5 overflow-hidden" style={{ background: 'linear-gradient(160deg, #080808 0%, #0d0d0d 40%, #0a0a0a 70%, #060606 100%)' }}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/25 to-transparent"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px]" style={{ background: 'radial-gradient(ellipse, rgba(192,192,192,0.06) 0%, transparent 70%)' }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Col 1 - Brand */}
          <div className="lg:col-span-1">
            <img
              src="https://static.readdy.ai/image/ee4ab35536c43b833838f71f4d3b75cf/64d89e01f0da6e76491ec791e8d4af31.png"
              alt="Miki Motors"
              className="h-40 w-auto object-contain mb-5"
            />
            <p className="text-white/40 text-sm leading-relaxed mb-6">
              Especialistas em mecânica de automóveis importados há mais de 11 anos em Campinas – SP.
            </p>
            <div className="flex gap-3">
              {[
                { icon: 'ri-facebook-fill', href: 'https://www.facebook.com/mikimotorsimports/', label: 'Facebook' },
                { icon: 'ri-instagram-line', href: 'https://www.instagram.com/mikimotorsimports/', label: 'Instagram' },
                { icon: 'ri-youtube-fill', href: 'https://www.youtube.com/channel/UCuv-P15BIG537o22DSL9Y2g', label: 'YouTube' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 text-white/50 hover:bg-[#C0C0C0]/20 hover:text-[#C0C0C0] transition-all duration-300 cursor-pointer"
                >
                  <i className={`${s.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Col 2 - Navigation */}
          <div>
            <h4 className="text-white font-bold text-sm mb-5 tracking-wider uppercase">Navegação</h4>
            <ul className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => handleNav(link.href)}
                    className="text-white/40 text-sm hover:text-[#C0C0C0] transition-colors duration-300 cursor-pointer text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 - Services */}
          <div>
            <h4 className="text-white font-bold text-sm mb-5 tracking-wider uppercase">Serviços</h4>
            <ul className="flex flex-col gap-3">
              {services.map((s) => (
                <li key={s}>
                  <button
                    onClick={() => handleNav('#servicos')}
                    className="text-white/40 text-sm hover:text-[#C0C0C0] transition-colors duration-300 cursor-pointer text-left"
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 - Contact */}
          <div>
            <h4 className="text-white font-bold text-sm mb-5 tracking-wider uppercase">Contato</h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line text-[#C0C0C0] text-base mt-0.5 flex-shrink-0"></i>
                <span className="text-white/40 text-sm leading-relaxed">
                  Av. Mal. Rondon, 2206 – Jardim Chapadão – Campinas – SP
                </span>
              </div>
              <a href="tel:+551941419185" className="flex items-center gap-3 cursor-pointer group">
                <i className="ri-phone-line text-[#C0C0C0] text-base flex-shrink-0"></i>
                <span className="text-white/40 text-sm group-hover:text-white/70 transition-colors">
                  (19) 4141-9185
                </span>
              </a>
              <a
                href="https://api.whatsapp.com/send/?phone=5519997889998&text&type=phone_number&app_absent=0"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 cursor-pointer group"
              >
                <i className="ri-whatsapp-line text-[#C0C0C0] text-base flex-shrink-0"></i>
                <span className="text-white/40 text-sm group-hover:text-white/70 transition-colors">
                  (19) 9 97889-998
                </span>
              </a>
              <a href="mailto:mikimotors@mikimotors.com.br" className="flex items-center gap-3 cursor-pointer group">
                <i className="ri-mail-line text-[#C0C0C0] text-base flex-shrink-0"></i>
                <span className="text-white/40 text-sm group-hover:text-white/70 transition-colors">
                  mikimotors@mikimotors.com.br
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/20 text-sm">
            © {new Date().getFullYear()} Miki Motors Imports. Todos os direitos reservados.
          </p>
          <p className="text-white/20 text-sm">
            Criado por{' '}
            <a
              href="https://guepardosites.link"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-[#C0C0C0] transition-colors duration-300 cursor-pointer"
            >
              guepardosites.link
            </a>
          </p>
        </div>

        {/* Big brand name */}
        <div className="mt-8 overflow-hidden">
          <p
            className="text-[#141414] text-[80px] md:text-[120px] font-black leading-none select-none text-center tracking-tighter"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            MIKIMOTORS
          </p>
        </div>
      </div>
    </footer>
  );
}
